import React, {
  createContext, useContext, useReducer, ReactNode,
} from 'react';

export interface GlobalAppState {
  teamIsChanging: string | false;
}

export type GlobalAppAction =
  | { type: 'SET_TEAM_CHANGING'; payload: string | false };

// Stato iniziale
export const initialAppState: GlobalAppState = {
  teamIsChanging: false,
};

// Reducer
export const globalReducer = (state: GlobalAppState, action: GlobalAppAction): GlobalAppState => {
  switch (action.type) {
    case 'SET_TEAM_CHANGING':
      return { ...state, teamIsChanging: action.payload };

    default:
      return state;
  }
};

// Context
interface GlobalAppContextType {
  state: GlobalAppState;
  dispatch: React.Dispatch<GlobalAppAction>;
}

const GlobalAppContext = createContext<GlobalAppContextType | undefined>(undefined);

// Provider
export const GlobalAppStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(globalReducer, initialAppState);

  const memoValue = React.useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <GlobalAppContext.Provider value={memoValue}>
      {children}
    </GlobalAppContext.Provider>
  );
};

// Hook per utilizzare lo stato globale
export const useGlobalAppState = () => {
  const context = useContext(GlobalAppContext);
  if (!context) {
    throw new Error('useGlobalAppState deve essere utilizzato all\'interno di GlobalStateProvider');
  }

  const { state, dispatch } = context;

  const setTeamIsChanging = (value: string | false) => {
    dispatch({ type: 'SET_TEAM_CHANGING', payload: value });
  };

  return {
    teamIsChanging: state.teamIsChanging,
    setTeamIsChanging,
  };
};
